import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API, apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { Nav } from "../components/Nav";
import { toast } from "sonner";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Play, FileText, Award, CheckCircle2, Circle, Lock, Download, X, Sparkles, ChevronDown } from "lucide-react";

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [submitText, setSubmitText] = useState("");
  const [enrollmentStatus, setEnrollmentStatus] = useState("loading");
  const [completionPopup, setCompletionPopup] = useState(null); // { type, lesson, module, pdfLessons }
  const [pdfPassword, setPdfPassword] = useState("");
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [expandedModules, setExpandedModules] = useState(new Set());
  const videoAreaRef = useRef(null);

  const toggleModule = (mid) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  };

  const standalones = (settings?.standalone_videos || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    loadCourse();
    loadAssignment();
    api.get("/my/pdf-password").then((r) => setPdfPassword(r.data.password)).catch(() => {});
    // eslint-disable-next-line
  }, [user, authLoading]);

  const loadCourse = async () => {
    try {
      const { data } = await api.get("/my/course/indian_fashion_illustration");
      setCourse(data);
      setEnrollmentStatus("enrolled");
      // Do NOT auto-select any lesson — student should see the full module list
      // first and pick what to watch. Video player only appears after a tap.
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e.response?.status === 403 && /ended/i.test(detail)) {
        setEnrollmentStatus("expired");
      } else if (e.response?.status === 403) {
        setEnrollmentStatus("not_enrolled");
      } else {
        toast.error("Failed to load course");
      }
    }
  };

  const loadAssignment = async () => {
    try {
      const { data } = await api.get("/my/assignment/indian_fashion_illustration");
      setAssignment(data);
      if (data.submission_text) setSubmitText(data.submission_text);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (!activeLesson) return;
    setVideoUrl(null);
    if (activeLesson.video_path) {
      const token = localStorage.getItem("token");
      setVideoUrl(`${API}/media/lesson/${activeLesson.id}/video?auth=${token}`);
    }
    // On mobile (below Tailwind `lg` breakpoint 1024px) auto-scroll to the video
    if (typeof window !== "undefined" && window.innerWidth < 1024 && videoAreaRef.current) {
      videoAreaRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeLesson]);

  const markComplete = async (lesson) => {
    // Standalones (Introduction, Hand Exercise) don't count in module progress
    if (lesson.isStandalone) {
      if (lesson.pdf_path) setCompletionPopup({ type: "lesson", lesson });
      else toast.success("Well done. Continue when you're ready.");
      return;
    }
    try {
      await api.post("/my/progress", { lesson_id: lesson.id, completed: true });
      toast.success("Lesson complete");
      // Find the module and figure out if this was the LAST lesson of the module
      const module = course?.modules?.find((m) => m.lessons.some((l) => l.id === lesson.id));
      if (module) {
        const completedIds = new Set(course.progress?.completed_lessons || []);
        completedIds.add(lesson.id);
        const isModuleComplete = module.lessons.every((l) => completedIds.has(l.id));
        const pdfLessons = module.lessons.filter((l) => l.pdf_path);
        if (isModuleComplete && pdfLessons.length > 0) {
          setCompletionPopup({ type: "module", module, pdfLessons });
        } else if (lesson.pdf_path) {
          setCompletionPopup({ type: "lesson", lesson });
        }
      } else if (lesson.pdf_path) {
        setCompletionPopup({ type: "lesson", lesson });
      }
      loadCourse();
    } catch { toast.error("Failed to update progress"); }
  };

  const downloadPdf = async (lesson) => {
    try {
      const path = lesson.isStandalone
        ? `/media/standalone/${lesson.id}/pdf`
        : `/media/lesson/${lesson.id}/pdf`;
      const res = await api.get(path, { responseType: "blob" });
      const pwd = res.headers["x-pdf-password"];
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${lesson.title}.pdf`; a.click();
      URL.revokeObjectURL(url);
      if (pwd) {
        toast.success(`Downloaded. PDF password: ${pwd}`, { duration: 15000, description: "This password is yours only. Do not share." });
      }
    } catch { toast.error("Worksheet not available"); }
  };

  const submitAssignment = async () => {
    try {
      await api.post("/my/assignment/indian_fashion_illustration/submit", { submission_text: submitText });
      toast.success("Assignment submitted for review");
      loadAssignment();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const downloadCertificate = async () => {
    try {
      const res = await api.get("/my/certificate/indian_fashion_illustration", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "certificate.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Certificate not yet ready"); }
  };

  if (enrollmentStatus === "loading") return <div className="p-10">Loading…</div>;

  if (enrollmentStatus === "expired") {
    return (
      <div className="bg-[#FAFAFA] min-h-screen">
        <Nav />
        <section className="max-w-2xl mx-auto px-6 md:px-10 pt-24 text-center" data-testid="access-expired">
          <p className="overline"><span className="gold-line mr-3" />Access Ended</p>
          <h1 className="font-serif-display font-light text-5xl mt-6">Your 3&#8209;month studio access has ended.</h1>
          <p className="mt-6 text-[#3a3a3a] max-w-md mx-auto leading-relaxed">
            Renew your enrolment to continue your lessons, keep your progress, and receive your signed certificate.
          </p>
          <Link to="/enroll" data-testid="renew-cta" className="btn-editorial mt-10">Renew Access</Link>
          <Link to="/contact" className="block overline link-underline mt-6">Need help? Contact us</Link>
        </section>
      </div>
    );
  }

  if (enrollmentStatus === "not_enrolled") {
    return (
      <div className="bg-[#FAFAFA] min-h-screen">
        <Nav />
        <section className="max-w-2xl mx-auto px-6 md:px-10 pt-24 text-center">
          <p className="overline"><span className="gold-line mr-3" />Not Yet Enrolled</p>
          <h1 className="font-serif-display font-light text-5xl mt-6">Your studio awaits.</h1>
          <p className="mt-6 text-[#3a3a3a]">Enrol in the course to unlock lessons, worksheets, and your certificate journey.</p>
          <Link to="/course" data-testid="dashboard-enroll-cta" className="btn-editorial mt-10">View the Course</Link>
        </section>
      </div>
    );
  }

  if (!course) return null;
  const progress = course.progress || { percent: 0, completed: 0, total: 0 };
  const completedIds = new Set(progress.completed_lessons || []);
  const canSubmit = progress.percent === 100;
  const certReady = assignment?.status === "approved";

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-24">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-8">
            <p className="overline"><span className="gold-line mr-3" />Your Studio</p>
            <h1 className="font-serif-display font-light text-4xl md:text-5xl mt-4">Welcome, {user?.name}.</h1>
            {typeof course.days_remaining === "number" && (
              <p
                className={`overline mt-4 ${course.days_remaining <= 14 ? "text-red-700" : "text-[#6B6B6B]"}`}
                data-testid="access-days-remaining"
              >
                {course.days_remaining > 0
                  ? `${course.days_remaining} day${course.days_remaining === 1 ? "" : "s"} remaining in your studio access`
                  : "Access ends today"}
              </p>
            )}
          </div>
          <div className="md:col-span-4 space-y-3">
            <div className="border border-black/10 p-6 bg-white">
              <div className="flex items-baseline justify-between mb-3">
                <p className="overline">Progress</p>
                <p className="font-serif-display text-3xl" data-testid="progress-percent">{progress.percent}%</p>
              </div>
              <Progress value={progress.percent} className="h-1 bg-black/10" />
              <p className="text-xs text-[#6B6B6B] mt-2">{progress.completed} of {progress.total} lessons</p>
            </div>
            {pdfPassword && (
              <div className="border border-[#D4AF37]/40 bg-white p-4" data-testid="pdf-password-card">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="overline mb-1">Your PDF Password</p>
                    <p className="font-mono text-lg tracking-widest text-[#D4AF37] select-all" data-testid="pdf-password-value">{pdfPassword}</p>
                  </div>
                  <button
                    data-testid="copy-pdf-password"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(pdfPassword); setPasswordCopied(true); setTimeout(() => setPasswordCopied(false), 1500); toast.success("Password copied"); }
                      catch { toast.info(`Password: ${pdfPassword}`); }
                    }}
                    className="text-[10px] uppercase tracking-widest border border-black/15 px-3 py-2 shrink-0 ml-3 hover:bg-black hover:text-white transition-colors"
                  >{passwordCopied ? "Copied ✓" : "Copy"}</button>
                </div>
                <p className="text-[10px] text-[#6B6B6B] mt-2 leading-snug">Use this to open any worksheet PDF. Yours only — do not share.</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Modules sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            {standalones.length > 0 && (
              <div>
                <p className="overline mb-3"><span className="gold-line mr-3" />Start Here</p>
                <div className="space-y-3" data-testid="standalone-list">
                  {standalones.map((s) => {
                    const active = activeLesson?.isStandalone && activeLesson?.id === s.id;
                    return (
                      <button
                        key={s.id}
                        data-testid={`standalone-${s.id}`}
                        onClick={() => setActiveLesson({ ...s, isStandalone: true })}
                        className={`w-full text-left border border-black/10 bg-white p-4 hover:bg-[#F2F0EB] transition-colors ${active ? 'bg-[#F2F0EB] border-[#D4AF37]' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <Sparkles size={16} className="text-[#D4AF37] shrink-0 mt-1" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium leading-tight">{s.title}</p>
                            {s.subtitle && <p className="text-xs text-[#6B6B6B] mt-1 leading-snug">{s.subtitle}</p>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <p className="overline">Modules</p>
            {course.modules.map((m, i) => {
              const isOpen = expandedModules.has(m.id) || m.lessons.some((l) => l.id === activeLesson?.id);
              return (
                <div key={m.id} className="border border-black/10 bg-white">
                  <button
                    data-testid={`module-toggle-${m.id}`}
                    onClick={() => toggleModule(m.id)}
                    className="w-full text-left p-5 border-b border-black/5 flex items-center justify-between gap-3 hover:bg-[#F2F0EB] transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="overline text-[10px]">Module {String(i + 1).padStart(2, '0')}</p>
                      <h3 className="font-serif-display text-lg mt-1 leading-tight">{m.title}</h3>
                    </div>
                    <ChevronDown size={18} className={`text-black/50 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <ul data-testid={`module-lessons-${m.id}`}>
                      {m.lessons.map((l, j) => {
                        const done = completedIds.has(l.id);
                        const active = activeLesson?.id === l.id;
                        const sectionLetter = String.fromCharCode(65 + j); // A, B, C…
                        return (
                          <li key={l.id}>
                            <button
                              data-testid={`lesson-${l.id}`}
                              onClick={() => setActiveLesson(l)}
                              className={`w-full text-left px-5 py-3 flex items-center gap-3 text-sm border-b border-black/5 transition-colors hover:bg-[#F2F0EB] ${active ? 'bg-[#F2F0EB]' : ''}`}
                            >
                              {done ? <CheckCircle2 size={16} className="text-[#D4AF37] shrink-0" /> : <Circle size={16} className="text-black/30 shrink-0" />}
                              <div className={`flex-1 min-w-0 ${done ? 'text-[#6B6B6B]' : ''}`}>
                                <p className="overline text-[9px] text-[#D4AF37]">Section {sectionLetter}</p>
                                <p className="mt-0.5 leading-tight">{l.title}</p>
                              </div>
                              <span className="overline text-[10px] shrink-0">{l.duration_display || `${l.duration_minutes}m`}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </aside>

          {/* Main video + assignment */}
          <div className="lg:col-span-8 space-y-8" ref={videoAreaRef}>
            {activeLesson && (
              <div>
                <p className="overline">Now Playing</p>
                <h2 className="font-serif-display text-3xl mt-2" data-testid="active-lesson-title">{activeLesson.title}</h2>
                <div className="mt-6 relative aspect-video bg-black border border-black/10">
                  {activeLesson.video_embed_url ? (
                    <iframe
                      key={activeLesson.id}
                      data-testid="lesson-embed-iframe"
                      title={activeLesson.title}
                      src={activeLesson.video_embed_url.replace(/autoplay=true/i, 'autoplay=false')}
                      className="w-full h-full absolute inset-0"
                      style={{ border: 0 }}
                      loading="lazy"
                      allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;fullscreen;screen-orientation;"
                      allowFullScreen
                      webkitallowfullscreen="true"
                      mozallowfullscreen="true"
                    />
                  ) : videoUrl ? (
                    <video
                      key={activeLesson.id}
                      controls
                      controlsList="nodownload"
                      onContextMenu={(e) => e.preventDefault()}
                      className="w-full h-full"
                      src={videoUrl}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-white/60 gap-3">
                      <Lock size={32} />
                      <p className="overline text-white/70">Video coming soon</p>
                      <p className="text-xs text-white/40 max-w-xs text-center">Your instructor is finalising this lesson's recording. Please check back shortly.</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 mt-6">
                  {!activeLesson.isStandalone && (
                    <button data-testid="mark-complete-btn" onClick={() => markComplete(activeLesson)} className="btn-editorial">
                      <CheckCircle2 size={16} className="mr-2" />Mark Complete
                    </button>
                  )}
                  {activeLesson.pdf_path && (
                    <button data-testid="download-pdf-btn" onClick={() => downloadPdf(activeLesson)} className="btn-editorial-outline">
                      <FileText size={16} className="mr-2" />Worksheet
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Assignment */}
            <div className="border border-black/10 bg-white p-8">
              <div className="flex items-baseline justify-between mb-2">
                <p className="overline"><span className="gold-line mr-3" />Final Assignment</p>
                {assignment?.status && assignment.status !== "not_submitted" && (
                  <span className="overline text-[#D4AF37]" data-testid="assignment-status">{assignment.status.replace("_", " ")}</span>
                )}
              </div>
              <h3 className="font-serif-display text-2xl mt-2">Your final illustration.</h3>
              <p className="text-sm text-[#3a3a3a] leading-relaxed mt-3">
                Complete all lessons, then submit your final illustration for review. Lata personally reviews each submission — once approved, your certificate becomes available.
              </p>

              {!canSubmit && (
                <p className="mt-6 text-xs text-[#6B6B6B]"><Lock size={12} className="inline mr-2" />Complete all lessons ({progress.completed}/{progress.total}) to unlock submission.</p>
              )}
              {canSubmit && assignment?.status !== "approved" && (
                <div className="mt-6 space-y-4">
                  <textarea
                    data-testid="assignment-text"
                    value={submitText}
                    onChange={(e) => setSubmitText(e.target.value)}
                    rows={5}
                    placeholder="Describe your illustration — the story, the drape, the palette."
                    className="w-full border border-black/15 bg-[#FAFAFA] p-4 text-sm outline-none focus:border-black"
                  />
                  <button data-testid="assignment-submit" onClick={submitAssignment} className="btn-editorial">
                    {assignment?.status === "pending_review" ? "Re-submit" : "Submit for Review"}
                  </button>
                </div>
              )}
              {assignment?.feedback && (
                <div className="mt-6 border-l-2 border-[#D4AF37] pl-4">
                  <p className="overline">Instructor Feedback</p>
                  <p className="mt-2 text-sm italic">{assignment.feedback}</p>
                </div>
              )}
            </div>

            {/* Certificate */}
            <div className="border border-black/10 bg-[#F2F0EB] p-8">
              <p className="overline"><span className="gold-line mr-3" />Certificate</p>
              <h3 className="font-serif-display text-2xl mt-2">Your Certificate of Completion.</h3>
              {certReady ? (
                <>
                  <p className="text-sm text-[#3a3a3a] mt-3">Your assignment has been approved by Lata. Download your signed certificate below.</p>
                  <button data-testid="download-cert-btn" onClick={downloadCertificate} className="btn-editorial mt-6">
                    <Award size={16} className="mr-2" />Download Certificate
                  </button>
                </>
              ) : (
                <p className="text-sm text-[#6B6B6B] mt-3"><Lock size={12} className="inline mr-2" />Available once your assignment is approved.</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* COMPLETION POPUP — auto-opens after Mark Complete when a PDF is available */}
      <Dialog open={!!completionPopup} onOpenChange={(o) => !o && setCompletionPopup(null)}>
        <DialogContent className="max-w-md p-0 border border-black/10 bg-[#FAFAFA]" data-testid="completion-popup">
          <div className="p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 size={20} className="text-[#D4AF37]" />
              <p className="overline">{completionPopup?.type === "module" ? "Chapter Complete" : "Lesson Complete"}</p>
            </div>
            <h3 className="font-serif-display text-3xl leading-tight" data-testid="completion-title">
              {completionPopup?.type === "module"
                ? <>You've finished<br /><span className="font-serif-italic italic text-[#D4AF37]">{completionPopup.module.title}</span>.</>
                : <>Beautifully done.</>}
            </h3>
            <p className="text-sm text-[#3a3a3a] mt-4 leading-relaxed">
              {completionPopup?.type === "module"
                ? "Download the worksheets from this chapter to keep with your studio notes."
                : "Download this lesson's worksheet to keep with your studio notes."}
            </p>
            <div className="mt-8 space-y-3">
              {completionPopup?.type === "module"
                ? completionPopup.pdfLessons.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => downloadPdf(l)}
                      data-testid={`popup-download-${l.id}`}
                      className="w-full flex items-center justify-between border border-black/15 bg-white px-4 py-3 hover:bg-[#F2F0EB] transition-colors text-left"
                    >
                      <span className="text-sm">{l.title}</span>
                      <Download size={14} className="text-[#D4AF37] shrink-0" />
                    </button>
                  ))
                : (
                    <button
                      onClick={() => downloadPdf(completionPopup.lesson)}
                      data-testid="popup-download-lesson"
                      className="btn-editorial w-full"
                    >
                      <Download size={14} className="mr-2" />Download Worksheet
                    </button>
                  )
              }
            </div>
            <button
              onClick={() => setCompletionPopup(null)}
              data-testid="popup-close"
              className="overline link-underline mt-8 block"
            >Continue to next lesson</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
